import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Blogs1 = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get('/api/blogs/public');
        // Sort blogs by date and mark the latest as featured
        const sortedBlogs = response.data.sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        const blogsWithFeatured = sortedBlogs.map((blog, index) => ({
          ...blog,
          featured: index === 0 // Mark the first (latest) blog as featured
        }));
        setBlogs(blogsWithFeatured);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blogs:', err);
        setError('Failed to load blog posts');
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  if (loading) {
    return (
      <section className="py-20 bg-cream dark:bg-black">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-cream dark:bg-black">
        <div className="container mx-auto px-4 text-center text-gray-600 dark:text-gray-400">
          <p>{error}</p>
        </div>
      </section>
    );
  }

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <section className="py-20 bg-cream dark:bg-black">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold mb-4 text-black dark:text-white"
          >
            Latest Insights
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Stay updated with our latest thoughts on technology, design, and digital transformation
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Featured Post */}
          {blogs.filter(blog => blog.featured).map(blog => (
            <motion.div 
              key={blog._id} 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="md:col-span-2 lg:col-span-2 group"
            >
              <a href={`/blog/${blog.slug}`} className="block hover:cursor-pointer relative overflow-hidden rounded-2xl shadow-lg transition-transform duration-300 group-hover:scale-[1.02]">
                <img
                  src={`${axios.defaults.baseURL}${blog.imageUrl}`}
                  alt={blog.title}
                  className="w-full h-[400px] object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/images/blog-placeholder.jpg';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                <div className="absolute bottom-0 p-8">
                  <span className="bg-primary text-white px-4 py-1 rounded-full text-sm mb-4 inline-block">
                    {blog.category}
                  </span>
                  <h3 className="text-3xl font-bold text-white mb-3">{blog.title}</h3>
                  <p className="text-gray-200 mb-4">{blog.excerpt}</p>
                  <div className="flex items-center text-white">
                    <span className="mr-4">{blog.author}</span>
                    <span>|</span>
                    <span className="ml-4">{formatDate(blog.createdAt)}</span>
                  </div>
                </div>
              </a>
            </motion.div>
          ))}

          {/* Regular Posts */}
          {blogs.filter(blog => !blog.featured).map(blog => (
            <motion.a 
              key={blog._id} 
              href={`/blog/${blog.slug}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group hover:cursor-pointer"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg transition-transform duration-300 group-hover:scale-[1.02]">
                <div className="relative h-48">
                  <img
                    src={`${axios.defaults.baseURL}${blog.imageUrl}`}
                    alt={blog.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/images/blog-placeholder.jpg';
                    }}
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-primary text-white px-3 py-1 rounded-full text-sm">
                      {blog.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-black dark:text-white mb-3">
                    {blog.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {blog.excerpt}
                  </p>
                  <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm">
                    <span className="mr-4">{blog.author}</span>
                    <span>|</span>
                    <span className="ml-4">{formatDate(blog.createdAt)}</span>
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/blog"
            className="bg-primary hover:bg-orange-dark text-white font-semibold py-3 px-8 rounded-full transition-colors duration-300"
          >
            View All Posts
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Blogs1;
